/** The chat column's width in px: dragged between `min` and `max`, reset to `default`. */
export const CHAT_COLUMN_WIDTH_PX = {
  default: 420,
  min: 320,
  max: 640,
} as const;

/** The chat never takes more than half the frame, whatever the px bounds say. */
const CHAT_COLUMN_MAX_PERCENT = 50;

/** Width of the divider between the chat and document columns, in px. */
export const CHAT_DIVIDER_WIDTH_PX = 16;

/** Percent sizes before the frame has been measured, for the first paint. */
const UNMEASURED_SIZES = {
  defaultSize: 30,
  minSize: 28,
  maxSize: 36,
} as const;

/**
 * Whether a drag actually moved the column. `onDragging` also fires on a plain
 * click, which should not count as a chosen width.
 *
 * @param widthAtDragStart - Size when the drag began, or null if unreadable
 * @param widthNow - Size now, or null if unreadable
 * @returns true when the two differ and both could be read
 */
export function shouldRememberChatWidth(
  widthAtDragStart: number | null,
  widthNow: number | null,
): boolean {
  if (widthAtDragStart === null || widthNow === null) return false;
  return widthAtDragStart !== widthNow;
}

/**
 * Marks a chat column the user resized. Separate from the library's own
 * `react-resizable-panels:` key, which it also writes for our default resize.
 */
const CHAT_WIDTH_CHOSEN_PREFIX = 'automation-editor:chat-width-chosen:';

/**
 * Whether the user has dragged this chat column to a width of their own.
 *
 * @param autoSaveId - The panel group's `autoSaveId`
 * @returns true when a drag has been recorded for that group
 */
export function hasChosenChatWidth(autoSaveId: string): boolean {
  try {
    return (
      localStorage.getItem(`${CHAT_WIDTH_CHOSEN_PREFIX}${autoSaveId}`) !== null
    );
  } catch {
    return false;
  }
}

/**
 * Records that the user dragged this chat column, so the px default stops
 * being applied over their choice on later visits.
 *
 * @param autoSaveId - The panel group's `autoSaveId`
 */
export function rememberChosenChatWidth(autoSaveId: string): void {
  try {
    localStorage.setItem(`${CHAT_WIDTH_CHOSEN_PREFIX}${autoSaveId}`, 'true');
  } catch {
    // Storage unavailable: the default keeps applying.
  }
}

/**
 * Forgets the user's width, so the px default applies again — what a
 * double-click on the divider asks for.
 *
 * @param autoSaveId - The panel group's `autoSaveId`
 */
export function forgetChosenChatWidth(autoSaveId: string): void {
  try {
    localStorage.removeItem(`${CHAT_WIDTH_CHOSEN_PREFIX}${autoSaveId}`);
  } catch {
    // Storage unavailable: nothing to forget.
  }
}

/** What `ResizablePanel` needs: shares of the group, in percent. */
export interface IChatPanelSizes {
  defaultSize: number;
  minSize: number;
  maxSize: number;
}

/**
 * Converts the chat column's px width into the percentages the panel group
 * sizes its panels in.
 *
 * @param params - Measured frame, and whether a document shares it
 * @param params.frameWidth - Width the panels divide (excluding the divider), in px; `null` until measured
 * @param params.hasDocument - Whether a document column is rendered beside the chat
 * @returns Percent sizes for the chat panel
 */
export function getChatPanelSizes({
  frameWidth,
  hasDocument,
}: {
  frameWidth: number | null;
  hasDocument: boolean;
}): IChatPanelSizes {
  if (!hasDocument) return { defaultSize: 100, minSize: 100, maxSize: 100 };
  if (frameWidth === null || frameWidth <= 0) return { ...UNMEASURED_SIZES };

  const toPercent = (px: number) => Math.min(100, (px / frameWidth) * 100);

  // Kept ordered (min <= default <= max) or the panel group warns and clamps.
  const maxSize = Math.min(
    CHAT_COLUMN_MAX_PERCENT,
    toPercent(CHAT_COLUMN_WIDTH_PX.max),
  );
  const minSize = Math.min(toPercent(CHAT_COLUMN_WIDTH_PX.min), maxSize);

  return {
    defaultSize: Math.min(
      Math.max(toPercent(CHAT_COLUMN_WIDTH_PX.default), minSize),
      maxSize,
    ),
    minSize,
    maxSize,
  };
}
